import React, { useState, useEffect, useContext, useRef } from "react";
import PropTypes from "prop-types";

import ScreamSkeleton from "../util/ScreamSkeleton";

import { connect } from "react-redux";
import { getScreams } from "../redux/actions/dataActions";
import { updatePassword } from "../redux/actions/userActions";
import Board from "../components/ArchivedChess/Board";
import { Chess } from "chess.js";
import Controls from "../components/ArchivedChess/Controls";
import Moves from "../components/ArchivedChess/Moves";
import { fetchGame, fetchNextGame, putGame } from "../util/api/game";
import { getMove, updateCommentIdInMoves } from "../util/Chess";
import AddComment from "../components/ArchivedChess/AddComment";
import Select from "react-select";
import { ChessSocialContext } from "../util/ChessSocialContext";
import { BOARD_THEMES } from '../components/WorkingChessLogic/BoardStyle.js';
import FAQData from '../constants/FAQData';

import { Box, Modal, Typography, Accordion, AccordionSummary, AccordionDetails } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

import { Chessboard } from 'react-chessboard';
import { usePremium } from '../util/PremiumFilter';

const Settings = (props) => {
  const { setActiveTab, activeTab } = useContext(ChessSocialContext);
  const [notifications, setNotifications] = useState({
    newFollowers: false,
    likesAndComments: false,
    appUpdates: false
  });
  const [boardSettings, setBoardSettings] = useState({
    showCoordinates: true,
    boardTheme: 'classic',
    pieceStyle: 'staunton'
  });
  const [privacySettings, setPrivacySettings] = useState({
    profileVisibility: 'public',
    defaultGameSharing: 'public',
    hideLiveChat: false
  });

  const { renderUserWithBadge } = usePremium();

  // Password update state
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState({});
  const [passwordUpdateSuccess, setPasswordUpdateSuccess] = useState(false);

  // Add a ref to track if we've already processed the URL parameter
  const initialTabSetRef = useRef(false);

  const [expandedPanel, setExpandedPanel] = useState(false);
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  // Add boardTheme state with local storage initialization
  const [boardTheme, setBoardTheme] = useState(() => {
    const savedTheme = localStorage.getItem('boardTheme');
    return savedTheme || 'blue'; // default to blue if no saved theme
  });

  // Add custom colors state with local storage initialization
  const [customColors, setCustomColors] = useState(() => {
    const savedCustomColors = localStorage.getItem('customColors');
    return savedCustomColors ? JSON.parse(savedCustomColors) : {
      dark: '#3066be',
      light: '#eeeed2'
    };
  });

  // Add coordinates state with local storage initialization
  const [showCoordinates, setShowCoordinates] = useState(() => {
    const savedCoordinates = localStorage.getItem('showCoordinates');
    return savedCoordinates === null ? true : savedCoordinates === 'true'; // default to true if not set
  });

  // Add show legal moves state with local storage initialization
  const [showLegalMoves, setShowLegalMoves] = useState(() => {
    const savedShowLegalMoves = localStorage.getItem('showLegalMoves');
    return savedShowLegalMoves === null ? true : savedShowLegalMoves === 'true'; // default to true if not set
  });

  // Add this state initialization with localStorage
  const [hideLiveChat, setHideLiveChat] = useState(() => {
    const savedHideChat = localStorage.getItem('hideLiveChat');
    return savedHideChat === 'true'; // default to false (chat visible) if not set
  });

  // Handle theme change
  const handleThemeChange = (e) => {
    const newTheme = e.target.value;
    setBoardTheme(newTheme);
    localStorage.setItem('boardTheme', newTheme);
  };

  // Handle custom color change
  const handleCustomColorChange = (type, value) => {
    const newCustomColors = { ...customColors, [type]: value };
    setCustomColors(newCustomColors);
    localStorage.setItem('customColors', JSON.stringify(newCustomColors));
    
    // If we're changing custom colors, make sure the theme is set to custom
    if (boardTheme !== 'custom') {
      setBoardTheme('custom');
      localStorage.setItem('boardTheme', 'custom');
    }
  };

  // Handle coordinates change
  const handleCoordinatesChange = (e) => {
    const newValue = e.target.checked;
    setShowCoordinates(newValue);
    localStorage.setItem('showCoordinates', newValue);
  };

  // Handle show legal moves change
  const handleShowLegalMovesChange = (e) => {
    const newValue = e.target.checked;
    setShowLegalMoves(newValue);
    localStorage.setItem('showLegalMoves', newValue);
  };

  // Add handler for live chat toggle
  const handleLiveChatChange = (e) => {
    const newValue = e.target.checked;
    setHideLiveChat(newValue);
    localStorage.setItem('hideLiveChat', newValue);
  };

  // Password update handlers
  const handleOpenPasswordModal = () => {
    setPasswordModalOpen(true);
    // Reset form state when opening modal
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordErrors({});
    setPasswordUpdateSuccess(false);
  };

  const handleClosePasswordModal = () => {
    setPasswordModalOpen(false);
  };

  const handlePasswordUpdate = (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = {};
    if (!currentPassword) errors.currentPassword = 'Current password is required';
    if (!newPassword) errors.newPassword = 'New password is required';
    if (newPassword && newPassword.length < 6) errors.newPassword = 'Password must be at least 6 characters';
    if (newPassword !== confirmPassword) errors.confirmPassword = 'Passwords do not match';
    
    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }
    
    // Call redux action to update password
    props.updatePassword(
      { currentPassword, newPassword },
      () => {
        // Success callback
        setPasswordUpdateSuccess(true);
        // Clear form
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setPasswordErrors({});
        // Close modal after 2 seconds
        setTimeout(() => {
          setPasswordModalOpen(false);
          setPasswordUpdateSuccess(false);
        }, 2000);
      },
      (err) => {
        // Error callback
        if (err.code === 'auth/wrong-password') {
          setPasswordErrors({ currentPassword: 'Current password is incorrect' });
        } else if (err.code === 'auth/weak-password') {
          setPasswordErrors({ newPassword: 'Password is too weak' });
        } else {
          setPasswordErrors({ general: 'Error updating password. Please try again.' });
        }
      }
    );
  };

  const modalBoxStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
  };

  useEffect(() => {
    // Only process URL parameter if we haven't done it yet
    if (!initialTabSetRef.current) {
      const queryParams = new URLSearchParams(window.location.search);
      const tabParam = queryParams.get('tab');
      
      // If tab parameter exists and is valid, set it as active tab
      if (tabParam && ['settings', 'billing', 'faq', 'contact', 'about'].includes(tabParam)) {
        setActiveTab(tabParam);
      } else {
        setActiveTab('settings');
      }
      
      // Mark that we've processed the URL parameter
      initialTabSetRef.current = true;
    }
  }, [setActiveTab]);

  const renderTabButtons = () => (
    <div style={{ 
      display: 'flex', 
      gap: '1.25rem', 
      width: '100%',
      maxWidth: '500px',
      justifyContent: 'center'
    }}>
      <button 
        onClick={() => setActiveTab('settings')}
        style={{ 
          color: activeTab === 'settings' ? 'gold' : 'white',
          fontSize: '1.2rem',
          background: 'none',
          border: 'none',
          borderBottom: activeTab === 'settings' ? '2px solid gold' : 'none',
          padding: '0.5rem 1rem',
          cursor: 'pointer'
        }}
      >
        Settings
      </button>
      {/* <button 
        onClick={() => setActiveTab('billing')}
        style={{ 
          color: activeTab === 'billing' ? 'gold' : 'white',
          fontSize: '1.2rem',
          background: 'none',
          border: 'none',
          borderBottom: activeTab === 'billing' ? '2px solid gold' : 'none',
          padding: '0.5rem 1rem',
          cursor: 'pointer'
        }}
      >
        Billing
      </button> */}
      <button 
        onClick={() => setActiveTab('faq')}
        style={{ 
          color: activeTab === 'faq' ? 'gold' : 'white',
          fontSize: '1.2rem',
          background: 'none',
          border: 'none',
          borderBottom: activeTab === 'faq' ? '2px solid gold' : 'none',
          padding: '0.5rem 1rem',
          cursor: 'pointer'
        }}
      >
        FAQ
      </button>
      <button 
        onClick={() => setActiveTab('contact')}
        style={{ 
          color: activeTab === 'contact' ? 'gold' : 'white',
          fontSize: '1.2rem',
          background: 'none',
          border: 'none',
          borderBottom: activeTab === 'contact' ? '2px solid gold' : 'none',
          padding: '0.5rem 1rem',
          cursor: 'pointer'
        }}
      >
        Contact
      </button>
      <button 
        onClick={() => setActiveTab('about')}
        style={{ 
          color: activeTab === 'about' ? 'gold' : 'white',
          fontSize: '1.2rem',
          background: 'none',
          border: 'none',
          borderBottom: activeTab === 'about' ? '2px solid gold' : 'none',
          padding: '0.5rem 1rem',
          cursor: 'pointer'
        }}
      >
        About
      </button>
    </div>
  );

  const renderBillingContent = () => (
    <div style={{ width: '100%', maxWidth: '500px' }}>
      {/* Current Plan */}
      <div className="settings-section" style={{ width: '100%', marginBottom: '1rem' }}>
        <div style={{ 
          backgroundColor: '#333',
          borderRadius: '8px',
          overflow: 'hidden',
          padding: '1rem 1.2rem'
        }}>
          <h2 style={{ 
            color: 'white', 
            fontSize: '1.8rem',
            margin: 0,
            borderBottom: '1px solid #444',
            paddingBottom: '1rem'
          }}>Current Plan</h2>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white', marginBottom: '1rem', marginTop: '1rem' }}>
            <div>
              <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>Premium Plan</div>
              <div style={{ color: '#888' }}>$9.99/month</div>
            </div>
            <div style={{ color: '#888', fontSize: '0.9rem' }}>Renews Jan 1, 2025</div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginTop: '1rem' }}>
            <button style={{ 
              backgroundColor: '#2196F3',
              color: 'white',
              padding: '0.8rem 1.2rem',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              flex: 1
            }}>
              Change Plan
            </button>
            <button style={{ 
              backgroundColor: '#2196F3',
              color: 'white',
              padding: '0.8rem 1.2rem',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              flex: 1
            }}>
              Update Payment Method
            </button>
            <button style={{ 
              backgroundColor: '#f44336',
              color: 'white',
              padding: '0.8rem 1.2rem',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              flex: 1
            }}>
              Cancel Plan
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch(activeTab) {
      case 'settings':
        return (
          <>
          {/* Board and Pieces Section */}
          <div className="settings-section" style={{ 
              width: '100%',
              marginBottom: '1rem'
            }}>
              <div style={{ 
                backgroundColor: '#333',
                borderRadius: '8px',
                overflow: 'hidden'
              }}>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <h2 style={{ 
                  color: 'white', 
                  fontSize: '1.8rem', 
                  padding: '1rem 1.2rem',
                  margin: 0,
                  borderBottom: '1px solid #444'
                }}>Chessboard</h2>
                <div style={{ maxHeight: '50px', maxWidth: '50px', overflow: 'hidden', marginRight: '1.3rem' }}>
                <Chessboard
                  boardWidth={200}
                  customDarkSquareStyle={boardTheme === 'custom' 
                    ? { background: customColors.dark } 
                    : BOARD_THEMES[boardTheme].dark}
                  customLightSquareStyle={boardTheme === 'custom' 
                    ? { background: customColors.light } 
                    : BOARD_THEMES[boardTheme].light}
                  position="K7/1k6/8/8/8/8/8/8 w - - 0 1"
                  showBoardNotation={showCoordinates}
                />
                </div>
                </div>
                <div style={{ padding: '1rem 1.2rem', color: 'white' }}>
                
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.8rem' }}>
                    <label>Board Theme</label>
                    <select 
                      value={boardTheme}
                      onChange={handleThemeChange}
                      style={{ padding: '0.5rem', width: '200px' }}
                    >
                      {Object.keys(BOARD_THEMES).filter(theme => theme !== 'custom').map(theme => (
                        <option key={theme} value={theme}>
                          {theme.charAt(0).toUpperCase() + theme.slice(1)}
                        </option>
                      ))}
                      <option value="custom">Custom Board Colors</option>
                    </select>
                  </div>
                  
                  {boardTheme === 'custom' && (
                    <div style={{ marginBottom: '0.8rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.8rem' }}>
                        <label>Dark Squares</label>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <div 
                            style={{ 
                              marginRight: '0.5rem',
                              width: '30px',
                              height: '30px',
                              borderRadius: '50%',
                              backgroundColor: customColors.dark,
                              border: '1px solid #555',
                              overflow: 'hidden',
                              position: 'relative',
                              cursor: 'pointer'
                            }}
                          >
                            <input 
                              type="color" 
                              value={customColors.dark}
                              onChange={(e) => handleCustomColorChange('dark', e.target.value)}
                              style={{ 
                                opacity: 0,
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                cursor: 'pointer'
                              }}
                            />
                          </div>
                          <input 
                            type="text" 
                            value={customColors.dark}
                            onChange={(e) => handleCustomColorChange('dark', e.target.value)}
                            placeholder="Pick Color"
                            style={{ padding: '0.5rem', width: '100px' }}
                          />
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.8rem' }}>
                        <label>Light Squares</label>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <div 
                            style={{ 
                              marginRight: '0.5rem',
                              width: '30px',
                              height: '30px',
                              borderRadius: '50%',
                              backgroundColor: customColors.light,
                              border: '1px solid #555',
                              overflow: 'hidden',
                              position: 'relative',
                              cursor: 'pointer'
                            }}
                          >
                            <input 
                              type="color" 
                              value={customColors.light}
                              onChange={(e) => handleCustomColorChange('light', e.target.value)}
                              style={{ 
                                opacity: 0,
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                cursor: 'pointer'
                              }}
                            />
                          </div>
                          <input 
                            type="text" 
                            value={customColors.light}
                            onChange={(e) => handleCustomColorChange('light', e.target.value)}
                            placeholder="Pick Color"
                            style={{ padding: '0.5rem', width: '100px' }}
                          />
                        </div>
                      </div>
                      <div style={{ 
                        color: '#aaa', 
                        fontSize: '0.85rem', 
                        marginTop: '0.5rem',
                        marginBottom: '1rem',
                        textAlign: 'left',
                        padding: '0 0.2rem'
                      }}>
                        Type a color name (e.g. red), hex code (e.g. #ff0050), or select the round color picker for more options.
                      </div>
                    </div>
                  )}
                  
                  
                  {/* <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.8rem' }}>
                    <label>Show Legal Moves</label>
                    <input 
                      type="checkbox" 
                      checked={showLegalMoves}
                      onChange={handleShowLegalMovesChange}
                      style={{ transform: 'scale(1.5)' }} 
                    />
                  </div> */}
                </div>
              </div>
            </div>
            {/* Account Management Section */}
            <div className="settings-section" style={{ 
              width: '100%',
              marginBottom: '1rem'
            }}>
              <div style={{ 
                backgroundColor: '#333',
                borderRadius: '8px',
                overflow: 'hidden'
              }}>
                <h2 style={{ 
                  color: 'white', 
                  fontSize: '1.8rem', 
                  padding: '1rem 1.2rem',
                  margin: 0,
                  borderBottom: '1px solid #444'
                }}>Account</h2>
                
                <div style={{ padding: '1rem 1.2rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ color: 'white' }}>
                      <div style={{ fontWeight: 'bold' }}>{renderUserWithBadge(props.user.credentials?.handle || 'Username')}</div>
                      <div>{props.user.credentials?.email || 'email@example.com'}</div>
                    </div>
                    <button 
                      className="change-button" 
                      style={{ 
                        marginTop: '0.5rem',
                        padding: '0.5rem 1rem',
                        background: 'transparent',
                        border: '2px solid white',
                        color: 'white',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                      onClick={handleOpenPasswordModal}
                    >
                      Change Password
                    </button>
                  </div>
                </div>
              </div>
            </div>

            

            {/* Privacy Section */}
            {/* <div className="settings-section" style={{ 
              width: '100%',
              marginBottom: '1rem'
            }}>
              <div style={{ 
                backgroundColor: '#333',
                borderRadius: '8px',
                overflow: 'hidden'
              }}>
                <h2 style={{ 
                  color: 'white', 
                  fontSize: '1.8rem', 
                  padding: '1rem 1.2rem',
                  margin: 0,
                  borderBottom: '1px solid #444'
                }}>Privacy & Notifications</h2>
                
                <div style={{ padding: '1rem 1.2rem', color: 'white' }}>
                  Remove this empty div since we commented out Profile Visibility
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.8rem' }}>
                    // commented out Profile Visibility select
                  </div>
                 
                 
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '0.8rem'
                  }}>
                    <label>Hide Live Chat</label>
                    <input 
                      type="checkbox" 
                      checked={hideLiveChat}
                      onChange={handleLiveChatChange}
                      style={{ transform: 'scale(1.5)' }} 
                    />
                  </div>

                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center'  // Remove marginBottom from last item
                  }}>
                    <label>Email Notifications</label>
                    <input type="checkbox" style={{ transform: 'scale(1.5)' }} />
                  </div>
                </div>
              </div>
            </div> */}

            {/* Notifications Section */}
            <div className="settings-section" style={{ 
              width: '100%',
              marginBottom: '1rem'
            }}>
              
            </div>

            {/* Footer Links */}
            <div style={{ 
              width: '100%', 
              maxWidth: '500px', 
              marginTop: '2rem', 
              color: '#888', 
              textAlign: 'center',
            }}>
              <a href="/Terms" target="_blank" rel="noopener noreferrer" style={{ color: '#888', textDecoration: 'none', marginRight: '5rem' }}>Terms & Conditions</a>
              <a href="/Privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#888', textDecoration: 'none' }}>Privacy Policy</a>
            </div>
          </>
        );
      case 'billing':
        return renderBillingContent();
      case 'faq':
        return (
          <div style={{ 
            width: '100%',
            maxWidth: '500px',
            color: 'white',
            backgroundColor: '#333',
            borderRadius: '8px',
            paddingTop: '1rem',
          }}>
            <Typography variant="h6" component="h2" gutterBottom style={{ 
              fontWeight: 'bold', 
              textAlign: 'center', 
              marginBottom: '20px',
              color: 'white'
            }}>
              Frequently Asked Questions
            </Typography>
            
            {FAQData.map((faq, index) => (
              <Accordion 
                key={index}
                expanded={expandedPanel === `panel${index}`}
                onChange={handleAccordionChange(`panel${index}`)}
                style={{
                  backgroundColor: '#444',
                  color: 'white',
                  marginBottom: '0.5rem'
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon style={{ color: 'white' }} />}
                  aria-controls={`faq-content-${index}`}
                  id={`faq-header-${index}`}
                >
                  <Typography style={{fontWeight: 'bold', color: 'white'}}>{faq.question}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography style={{ color: 'white' }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </div>
        );
      case 'contact':
        return (
          <div style={{ 
            width: '100%',
            maxWidth: '500px',
            color: 'white'
          }}>
            <div className="settings-section" style={{ width: '100%', marginBottom: '1rem' }}>
        <div style={{ 
          backgroundColor: '#333',
          borderRadius: '8px',
          overflow: 'hidden',
          padding: '1rem 1.2rem'
        }}>
          <h2 style={{ 
            color: 'white', 
            fontSize: '1.8rem',
            margin: 0,
            borderBottom: '1px solid #444',
            paddingBottom: '1rem'
          }}>Contact Us</h2>
          <p>For all inquiries, including support, billing, and questions about advertising chess on our platform, please contact:<br /><br /><a style={{ color: 'gold' }} href="mailto:info@chessthread.com">info@chessthread.com</a></p>
         
        </div>
      </div>            
          </div>
        );
      case 'about':
        return (
          <div style={{ 
            width: '100%',
            maxWidth: '500px',
            color: 'white'
          }}>
           <div className="settings-section" style={{ width: '100%', marginBottom: '1rem' }}>
        <div style={{ 
          backgroundColor: '#333',
          borderRadius: '8px',
          overflow: 'hidden',
          padding: '1rem 1.2rem'
        }}>
          <h2 style={{ 
            color: 'white', 
            fontSize: '1.8rem',
            margin: 0,
            borderBottom: '1px solid #444',
            paddingBottom: '1rem'
          }}>Our Mission</h2>
          
          <p>
  <span style={{ fontStyle: 'italic' }}>Make sharing chess effortless.</span><br /><br />Whether you're creating puzzles from your own games, testing variations on other's, or kicking back to watch the magic unfold within the community, we've built the easiest way for you to share and enjoy chess online.<br /><br /><span style={{ fontStyle: 'italic', color: 'gold' }}>If it can live on a chessboard, it belongs here.</span>
  {/* <br /><br />Love what we're building? Support us by <span onClick={() => window.open('/premium', '_blank')} style={{ color: 'gold', cursor: 'pointer' }}>going premium—</span>or just share it with your chess friends! */}

</p>

        </div>
      </div>   
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="app-container">
      <div className="explore-container main-column" style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        overflowY: "auto",
        padding: '2rem',
        position: 'relative',
        gap: '1rem'
      }}>
        {renderTabButtons()}
        <div style={{ 
          width: '100%',
          maxWidth: '500px',
          paddingBottom: '60px'
        }}>
          {renderContent()}
        </div>
        
        {/* Password Update Modal */}
        <Modal
          open={passwordModalOpen}
          onClose={handleClosePasswordModal}
          aria-labelledby="password-update-modal"
          aria-describedby="update-your-password"
          style={{
          }}
        >
          <Box sx={{
            position: 'relative',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: '#333',
            border: '3px solid #666',
            boxShadow: 24,
            p: 4,
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {passwordUpdateSuccess ? (
              <div style={{ 
                textAlign: 'center', 
                color: '#4caf50',
                padding: '20px'
              }}>
                <Typography variant="h6" style={{ color: '#ccc', marginBottom: '10px' }}>
                  Success!
                </Typography>
                <Typography style={{ color: 'white' }}>
                  Your password has been updated successfully.
                </Typography>
              </div>
            ) : (
              <>
                <Typography variant="h6" component="h2" style={{ 
                  color: '#ccc', 
                  marginBottom: '20px',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  Change Your Password
                </Typography>
                
                <form onSubmit={handlePasswordUpdate} style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '15px' 
                }}>
                  <div>
                    <input
                      type="password"
                      placeholder="Current Password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      style={{ 
                        width: '100%',
                        padding: '12px',
                        fontSize: '16px',
                        border: passwordErrors.currentPassword ? '2px solid #888' : '2px solid #666',
                        borderRadius: '4px',
                        backgroundColor: '#444',
                        color: 'white'
                      }}
                    />
                    {passwordErrors.currentPassword && (
                      <div style={{ color: '#aaa', fontSize: '14px', marginTop: '5px' }}>
                        {passwordErrors.currentPassword}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <input
                      type="password"
                      placeholder="New Password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      style={{ 
                        width: '100%',
                        padding: '12px',
                        fontSize: '16px',
                        border: passwordErrors.newPassword ? '2px solid #888' : '2px solid #666',
                        borderRadius: '4px',
                        backgroundColor: '#444',
                        color: 'white'
                      }}
                    />
                    {passwordErrors.newPassword && (
                      <div style={{ color: '#aaa', fontSize: '14px', marginTop: '5px' }}>
                        {passwordErrors.newPassword}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <input
                      type="password"
                      placeholder="Confirm New Password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      style={{ 
                        width: '100%',
                        padding: '12px',
                        fontSize: '16px',
                        border: passwordErrors.confirmPassword ? '2px solid #888' : '2px solid #666',
                        borderRadius: '4px',
                        backgroundColor: '#444',
                        color: 'white'
                      }}
                    />
                    {passwordErrors.confirmPassword && (
                      <div style={{ color: '#aaa', fontSize: '14px', marginTop: '5px' }}>
                        {passwordErrors.confirmPassword}
                      </div>
                    )}
                  </div>
                  
                  {passwordErrors.general && (
                    <div style={{ 
                      color: '#aaa', 
                      fontSize: '14px', 
                      textAlign: 'center',
                      padding: '10px',
                      backgroundColor: 'rgba(255, 0, 0, 0.1)',
                      borderRadius: '4px'
                    }}>
                      {passwordErrors.general}
                    </div>
                  )}
                  
                  <div style={{ display: 'flex', justifyContent: 'space-within', marginTop: '10px', gap: '30px', fontSize: '' }}>
                    <button
                      type="button"
                      onClick={handleClosePasswordModal}
                      style={{ 
                        padding: '10px 20px',
                        backgroundColor: 'transparent',
                        border: '2px solid #666',
                        color: '#aaa',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '16px'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      style={{ 
                        padding: '10px 20px',
                        background: 'transparent',
                        border: '2px solid #888',
                        color: '#ccc',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '16px'
                      }}
                    >
                      Change
                    </button>
                  </div>
                </form>
              </>
            )}
          </Box>
        </Modal>
      </div>
    </div>
  );
};

Settings.propTypes = {
  getScreams: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  updatePassword: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  UI: PropTypes.object.isRequired
};

const mapStateToProps = (state) => ({
  data: state.data,
  user: state.user,
  UI: state.UI
});

export default connect(mapStateToProps, { getScreams, updatePassword })(Settings);
